import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Select,
  Flex,
  Input,
  Checkbox,
  Form,
  Row,
  Col,
  Typography,
  Button,
  notification,
  InputNumber,
} from "antd";
import styles from "pages/admin/referenceBooks/stages/stages.module.scss";
import clsx from "clsx";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { useParams, useNavigate } from "react-router-dom";
import { create } from "zustand";

const { Text } = Typography;

type ComponentProps = {};

type Stage = {
  id: number;
  sort_order: number;
  name: string;
  status: "active" | "unactive";
  stage_group_id: number;
  units?: { id: number; name: string; status: "active" | "unactive" }[];
  operations?: {
    id: number;
    stage_id: number;
    name: string;
    completion_percentage: number;
  }[];
  styles?: {
    [className: string]: string;
  };
};

type StageGroup = {
  id: number;
  name: string;
  status: "active" | "unactive";
  styles?: {
    [className: string]: string;
  };
};

type StageUnit = {
  id: number;
  name: string;
  status: "active" | "unactive";
  styles?: {
    [className: string]: string;
  };
};

type StageOperation = {
  id: string | number;
  name: string;
  completion_percentage: string;
  isEditMode: boolean;
  styles?: {
    [className: string]: string;
  };
  isMobileMode?: boolean;
};

const { Title } = Typography;

const defaultOperationsList = [
  {
    id: "new",
    sort_order: "",
    name: "",
    completion_percentage: "",
    isEditMode: true,
  },
];

type StagesStoreState = {
  list: { isLoaded: boolean; data: Stage[] };
  setList: (list: { isLoaded: boolean; data: Stage[] }) => void;
  stageGroupsList: { isLoaded: boolean; data: StageGroup[] };
  setStageGroupsList: (list: { isLoaded: boolean; data: StageGroup[] }) => void;
  stageUnitsList: { isLoaded: boolean; data: StageUnit[] };
  setStageUnitsList: (list: { isLoaded: boolean; data: StageUnit[] }) => void;
  putIds: number[];
  setPutIds: (update: (state: number[]) => number[]) => void;
  isPostInProcess: boolean;
  setIsPostInProcess: (isPostInProcess: boolean) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
  isError: boolean;
  setIsError: (isError: boolean) => void;
  operationsList: StageOperation[];
  setOperationsList: (
    update: (state: StageOperation[]) => StageOperation[]
  ) => void;
};

const useStagesStore = create<StagesStoreState>((set) => ({
  list: {
    isLoaded: false,
    data: [],
  },
  setList: (list) => set({ list }),
  stageGroupsList: {
    isLoaded: false,
    data: [],
  },
  setStageGroupsList: (stageGroupsList) => set({ stageGroupsList }),
  stageUnitsList: {
    isLoaded: false,
    data: [],
  },
  setStageUnitsList: (stageUnitsList) => set({ stageUnitsList }),
  putIds: [],
  setPutIds: (update) =>
    set((state) => ({
      putIds: update(state.putIds),
    })),
  isPostInProcess: false,
  setIsPostInProcess: (isPostInProcess) => set({ isPostInProcess }),
  isSubmittable: false,
  setIsSubmittable: (isSubmittable) => set({ isSubmittable }),
  isError: false,
  setIsError: (isError) => set({ isError }),
  operationsList: defaultOperationsList,
  setOperationsList: (update) =>
    set((state) => ({
      operationsList: update(state.operationsList),
    })),
}));

const Page: React.FC<ComponentProps> = () => {
  const { "*": subRoute } = useParams();
  const navigate = useNavigate();

  const isAddMode = useMemo(() => {
    if (subRoute === "add") return true;

    return false;
  }, [subRoute]);

  const isEditMode = useMemo(() => {
    if (!subRoute?.startsWith("edit/")) return false;

    const id = parseInt(subRoute.split("/")[1], 10);

    return isNaN(id) ? false : id;
  }, [subRoute]);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const {
    list,
    setList,
    stageGroupsList,
    setStageGroupsList,
    stageUnitsList,
    setStageUnitsList,
    putIds,
    setPutIds,
    isPostInProcess,
    setIsPostInProcess,
    isSubmittable,
    setIsSubmittable,
    isError,
    setIsError,
    operationsList,
    setOperationsList,
  } = useStagesStore((store) => store);

  const listRef = useRef(list);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    if (!isAddMode && !isEditMode) return;

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isAddMode, isEditMode, form, values, setIsError, setIsSubmittable]);

  //  Get all stages
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/stages?order=sort_order,order=id`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: Stage[] = data.records;

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (list.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList, list.isLoaded]
  );

  //  Get all stage groups
  const getStageGroupsList = useCallback(async () => {
    const fetchData = async () => {
      try {
        const url = `${apiDomain}/api/records/stage_groups?filter=status,eq,active`;
        const options: RequestInit = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        };

        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        const data = await response.json();

        if (!data?.records) throw new Error();

        const records: Stage[] = data.records;

        setStageGroupsList({ isLoaded: true, data: records });
      } catch (error) {
        logout();
      }
    };

    if (stageGroupsList.isLoaded) return;

    await fetchData();
  }, [setStageGroupsList, stageGroupsList.isLoaded]);

  //  Get all stage units
  const getStageUnitsList = useCallback(async () => {
    const fetchData = async () => {
      try {
        const url = `${apiDomain}/api/records/stage_units?filter=status,eq,active`;
        const options: RequestInit = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        };

        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        const data = await response.json();

        if (!data?.records) throw new Error();

        const records: Stage[] = data.records;

        setStageUnitsList({ isLoaded: true, data: records });
      } catch (error) {
        logout();
      }
    };

    if (stageUnitsList.isLoaded) return;

    await fetchData();
  }, [setStageUnitsList, stageUnitsList.isLoaded]);

  //  When started, get all stages and stage groups and stage units
  useEffect(() => {
    getList();
    getStageGroupsList();
    getStageUnitsList();
  }, [getList, getStageGroupsList, getStageUnitsList]);

  //  Edit button sets edit mode or reset it
  const handleEditButton = useCallback(
    (id?: number) => {
      navigate(`/reference-books/stages/edit/${id}`);

      if (!id) setOperationsList(() => defaultOperationsList);
    },
    [navigate, setOperationsList]
  );

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    const item = listRef.current.data.find((item) => item.id === isEditMode);

    if (!item) form.resetFields();

    if (item) {
      form.setFieldsValue({
        name: item.name,
        sort_order: item.sort_order,
        status: item.status === "active",
        stageUnits: item.units ? item.units.map((unit) => unit.id) : [],
        stageGroup: item.stage_group_id,
      });

      const operationsList: StageOperation[] = [];

      if (item?.operations?.length)
        item.operations.forEach((operation) => {
          operationsList.push({
            id: operation.id,
            name: operation.name,
            completion_percentage: operation.completion_percentage.toString(),
            isEditMode: false,
          });
        });

      operationsList.push(defaultOperationsList[0]);

      setOperationsList(() => operationsList);
    }

    setIsError(false);

    setTimeout(() => {
      form
        .validateFields({ validateOnly: true })
        .then(() => setIsSubmittable(true))
        .catch(() => setIsSubmittable(false));
    }, 0);
  }, [
    isEditMode,
    form,
    list.isLoaded,
    setIsError,
    setIsSubmittable,
    setOperationsList,
  ]);

  //  Add button sets add mode or reset it
  const handleAddButton = useCallback(() => {
    navigate(`/reference-books/stages/add`);
  }, [navigate]);

  const handleBackButton = useCallback(() => {
    navigate(`/reference-books/stages`);
  }, [navigate]);

  const isInProcess = useMemo(() => {
    if (!!isAddMode && !!isPostInProcess) return true;

    if (!!isEditMode && !!putIds.includes(isEditMode)) return true;

    return false;
  }, [isAddMode, isPostInProcess, isEditMode, putIds]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;

    const fetchData = async () => {
      setIsError(false);

      const editModeId = isEditMode;

      if (editModeId)
        setPutIds((state) => {
          return [...state, editModeId];
        });

      if (!editModeId) setIsPostInProcess(true);

      const url = editModeId
        ? `${apiDomain}/api/records/stages/${editModeId}`
        : `${apiDomain}/api/records/stages`;

      const options: RequestInit = {
        method: editModeId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.name.trim(),
          sort_order: values.sort_order,
          status: values.status ? "active" : "unactive",
          stage_group_id: values.stageGroup,
          units: values.stageUnits,
          operations: operationsList,
        }),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        getList(true);

        form.resetFields();
        form
          .validateFields({ validateOnly: true })
          .then(() => setIsSubmittable(true))
          .catch(() => setIsSubmittable(false));

        navigate(`/reference-books/stages`);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (!editModeId) setIsPostInProcess(false);

        setOperationsList(() => defaultOperationsList);
      } catch (error) {
        setIsError(true);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (!editModeId) setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    isEditMode,
    form,
    values,
    operationsList,
    isInProcess,
    navigate,
    setIsPostInProcess,
    setIsError,
    getList,
    setOperationsList,
    setPutIds,
    setIsSubmittable,
  ]);

  //  Change status request
  const handleChangeStatus = useCallback(
    (id: number) => {
      const currentItem = list.data.find((item) => item.id === id);

      if (!currentItem) return;

      setPutIds((state) => {
        return [...state, currentItem.id];
      });

      const fetchData = async () => {
        const url = `${apiDomain}/api/records/stages/${currentItem.id}`;
        const options: RequestInit = {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: currentItem.status === "active" ? "unactive" : "active",
          }),
          credentials: "include",
        };

        try {
          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          await getList(true);

          setPutIds((state) => {
            return state.filter((id) => id !== currentItem.id);
          });
        } catch (error) {
          setPutIds((state) => {
            return state.filter((id) => id !== currentItem.id);
          });

          openNotificationWithError({
            message: "Ошибка при изменении статуса",
          });
        }
      };

      fetchData();
    },
    [list, getList, setPutIds, openNotificationWithError]
  );

  const handleEditStageOperation = useCallback(
    (id?: string) => {
      setOperationsList((operationsList) => {
        const newOperationsList = [...operationsList];

        const currentOperation = newOperationsList.find(
          (operation) => operation.id.toString() === id
        );

        if (currentOperation) currentOperation.isEditMode = true;

        return newOperationsList;
      });
    },
    [setOperationsList]
  );

  const handleDeleteStageOperation = useCallback(
    (id?: string) => {
      setOperationsList((operationsList) => {
        const newOperationsList = [...operationsList].filter(
          (operation) => operation.id.toString() !== id
        );

        return newOperationsList;
      });
    },
    [setOperationsList]
  );

  const handleSaveStageOperation = useCallback(
    (id?: string) => {
      setOperationsList((operationsList) => {
        const newOperationsList = [...operationsList];

        const currentOperation = newOperationsList.find(
          (operation) => operation.id.toString() === id
        );

        if (currentOperation) {
          if (id === "new") {
            const biggestId = newOperationsList.reduce((result, current) => {
              const match = current.id.toString().match(/^new-(\d+)$/);

              if (!match) return result;

              const number = parseInt(match[1], 10);

              return number > result ? number : result;
            }, 0);

            newOperationsList.splice(newOperationsList.length - 1, 0, {
              ...currentOperation,
              id: `new-${biggestId + 1}`,
              isEditMode: false,
            });

            currentOperation.id = "new";
            currentOperation.isEditMode = true;
            currentOperation.completion_percentage = "";
            currentOperation.name = "";
          }

          if (id !== "new") {
            currentOperation.isEditMode = false;
          }
        }

        return newOperationsList;
      });
    },
    [setOperationsList]
  );

  const handleEditStageOperationName = useCallback(
    (id?: string, value?: string) => {
      setOperationsList((operationsList) => {
        const newOperationsList = [...operationsList];

        const currentOperation = newOperationsList.find(
          (operation) => operation.id.toString() === id
        );

        if (currentOperation) currentOperation.name = value ? value : "";

        return newOperationsList;
      });
    },
    [setOperationsList]
  );

  const handleEditStageOperationCompletionPercentage = useCallback(
    (id?: string, value?: string) => {
      setOperationsList((operationsList) => {
        const newOperationsList = [...operationsList];

        const currentOperation = newOperationsList.find(
          (operation) => operation.id.toString() === id
        );

        if (currentOperation) {
          const filteredValue = value
            ? value === "" || /^\d+$/.test(value)
              ? value
              : currentOperation.completion_percentage
            : "";

          currentOperation.completion_percentage = filteredValue;
        }

        return newOperationsList;
      });
    },
    [setOperationsList]
  );

  const totalPercentage = useMemo(() => {
    return operationsList.reduce((sum, operation) => {
      const percentage = operation.completion_percentage;
      const numericValue = parseInt(percentage, 10);

      if (!isNaN(numericValue)) return sum + numericValue;

      return sum;
    }, 0);
  }, [operationsList]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [operationsList, form, setIsSubmittable]);

  const validatePercentage = useCallback(
    async (test: any, test2: any) => {
      const isNotSaved = operationsList.filter(
        (operation) =>
          operation.id.toString() !== "new" && !!operation.isEditMode
      );

      if (!isNotSaved) return Promise.reject();

      const totalSaved = operationsList.reduce((sum, operation) => {
        if (operation.isEditMode) return sum;

        const percentage = operation.completion_percentage;
        const numericValue = parseInt(percentage, 10);

        if (!isNaN(numericValue)) return sum + numericValue;

        return sum;
      }, 0);

      if (totalSaved !== 100) return Promise.reject();

      return Promise.resolve();
    },
    [operationsList]
  );

  const operationsColumns: ColumnsType = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "Операция",
        key: "name",
        width: "50%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as StageOperation;

          if (record.isEditMode)
            return (
              <div>
                <Input
                  size="large"
                  name={`name[${record.id}]`}
                  onChange={(e) =>
                    handleEditStageOperationName(
                      record.id.toString(),
                      e.target.value
                    )
                  }
                  value={record.name}
                  disabled={
                    !stageGroupsList.isLoaded ||
                    !stageUnitsList.isLoaded ||
                    isInProcess
                  }
                />
              </div>
            );

          return <div>{record.name}</div>;
        },
      },
      {
        title: "% готовности заказа",
        key: "completion_percentage",
        width: "50%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as StageOperation;

          if (record.isEditMode)
            return (
              <div>
                <Input
                  size="large"
                  name={`completion_percentage[${record.id}]`}
                  onChange={(e) =>
                    handleEditStageOperationCompletionPercentage(
                      record.id.toString(),
                      e.target.value
                    )
                  }
                  value={record.completion_percentage}
                  disabled={
                    !stageGroupsList.isLoaded ||
                    !stageUnitsList.isLoaded ||
                    isInProcess
                  }
                />
              </div>
            );

          return <div>{record.completion_percentage}</div>;
        },
      },
      {
        key: "edit",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as StageOperation;
          const recordStyles = record.styles;

          if (!!record.isMobileMode && !!record.isEditMode) return null;

          return (
            <>
              <Button
                onClick={() => handleEditStageOperation(record.id.toString())}
                type={"primary"}
                className={clsx(
                  recordStyles?.actionButton,
                  !!record.isEditMode && recordStyles?.isHidden
                )}
                disabled={
                  !stageGroupsList.isLoaded ||
                  !stageUnitsList.isLoaded ||
                  isInProcess
                }
              >
                Изменить
              </Button>
            </>
          );
        },
      },
      {
        key: "delete",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as StageOperation;
          const recordStyles = record.styles;

          if (record.isEditMode)
            return (
              <>
                <Button
                  onClick={() => handleSaveStageOperation(record.id.toString())}
                  type={"primary"}
                  className={recordStyles?.actionButton}
                  disabled={
                    !record.name ||
                    !record.completion_percentage ||
                    !stageGroupsList.isLoaded ||
                    !stageUnitsList.isLoaded ||
                    isInProcess
                  }
                >
                  Сохранить
                </Button>
              </>
            );

          return (
            <>
              <Button
                onClick={() => handleDeleteStageOperation(record.id.toString())}
                type={"primary"}
                danger={true}
                className={styles.actionButton}
                disabled={
                  !stageGroupsList.isLoaded ||
                  !stageUnitsList.isLoaded ||
                  isInProcess
                }
              >
                Удалить
              </Button>
            </>
          );
        },
      },
    ];

    return result;
  }, [
    stageGroupsList,
    stageUnitsList,
    isInProcess,
    handleSaveStageOperation,
    handleEditStageOperationName,
    handleEditStageOperationCompletionPercentage,
    handleEditStageOperation,
    handleDeleteStageOperation,
  ]);

  //  Set columns for table
  const columns: ColumnsType = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        sorter: {
          compare: (a: unknown, b: unknown) =>
            (a as Stage).id - (b as Stage).id,
        },
        render: (value: any, untypedRecord: unknown) => {
          const record = untypedRecord as Stage;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.id}
            </div>
          );
        },
      },
      {
        title: "Сортировка",
        key: "sort_order",
        sorter: {
          compare: (a: unknown, b: unknown) =>
            (a as Stage).sort_order - (b as Stage).sort_order,
        },
        render: (value: any, untypedRecord: unknown) => {
          const record = untypedRecord as Stage;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.sort_order}
            </div>
          );
        },
      },
      {
        title: "Название",
        key: "name",
        width: "100%",
        render: (value: any, untypedRecord: unknown) => {
          const record = untypedRecord as Stage;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.name}
            </div>
          );
        },
      },
      {
        key: "edit",
        render: (value: any, untypedRecord: unknown) => {
          const record = untypedRecord as Stage;
          const recordStyles = record.styles;

          return (
            <>
              <Button
                onClick={() => handleEditButton(record.id)}
                type={record.status === "active" ? "primary" : "default"}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
              >
                Изменить
              </Button>
            </>
          );
        },
      },
      {
        key: "changeStatus",
        render: (value: any, untypedRecord: unknown) => {
          const record = untypedRecord as Stage;
          const recordStyles = record.styles;

          return (
            <>
              <Button
                onClick={() => handleChangeStatus(record.id)}
                type={record.status === "active" ? "primary" : "default"}
                danger={record.status === "active"}
                loading={putIds.includes(record.id)}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
              >
                {record.status === "active" ? "Выключить" : "Включить"}
              </Button>
            </>
          );
        },
      },
    ];

    return result;
  }, [putIds, isInProcess, handleEditButton, handleChangeStatus]);

  return (
    <MobileInnerWrapper>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} className={styles.title}>
            {isAddMode
              ? `Добавление этапа работ`
              : isEditMode
              ? `Редактирование этапа работ №${isEditMode}`
              : `Этапы работ`}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={
              isAddMode || isEditMode ? handleBackButton : handleAddButton
            }
            disabled={isInProcess}
          >
            {isAddMode || isEditMode
              ? `Вернуться к списку`
              : `Добавить этап работ`}
          </Button>
        </Col>
      </Row>
      {(!!isAddMode || !!isEditMode) &&
        ((!!stageGroupsList.isLoaded && !stageGroupsList.data.length) ||
          (!!stageUnitsList.isLoaded && !stageUnitsList.data.length)) && (
          <Typography>
            <Text type="danger">
              Для создания этапа работ необходимо добавить группы этапов работ и
              единицы измерения.
            </Text>
          </Typography>
        )}
      {(!!isAddMode || !!isEditMode) &&
        !!stageGroupsList.data.length &&
        !!stageUnitsList.data.length && (
          <>
            <Flex vertical gap={16}>
              <Form
                layout={"vertical"}
                form={form}
                onFinish={handleFinish}
                initialValues={{
                  name: "",
                  status: true,
                  stageUnits: [],
                }}
              >
                <Form.Item
                  name="sort_order"
                  label="Сортировка"
                  validateTrigger={false}
                  required={false}
                >
                  <InputNumber
                    size="large"
                    name="sort_order"
                    min={0}
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="name"
                  label="Название этапа работ"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, введите название этапа работ!",
                    },
                    {
                      validator: validatePercentage,
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Input
                    size="large"
                    name="name"
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="stageGroup"
                  label="Группа этапов работ"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, выберите группу этапов!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Select
                    placeholder="Выберите группу этапов"
                    disabled={isInProcess || !list.isLoaded}
                  >
                    {stageGroupsList.data.map((stageGroup) => {
                      return (
                        <Select.Option
                          key={stageGroup.id}
                          value={stageGroup.id}
                        >
                          {stageGroup.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="stageUnits"
                  label="Единицы измерения"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, выберите единицу измерения!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Checkbox.Group>
                    {stageUnitsList.data.map((stageUnit) => (
                      <Checkbox
                        key={stageUnit.id}
                        value={stageUnit.id}
                        disabled={isInProcess || !list.isLoaded}
                      >
                        {stageUnit.name}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
                <Table
                  dataSource={operationsList}
                  columns={operationsColumns}
                  rowKey="id"
                  pagination={false}
                  className={styles.operationsTable}
                  styles={styles}
                />
                <div className={styles.totalPercentage}>
                  <Text>
                    Итого:{" "}
                    <Text
                      className={
                        totalPercentage > 100
                          ? styles.redText
                          : totalPercentage === 100
                          ? styles.greenText
                          : undefined
                      }
                    >
                      {totalPercentage}%
                    </Text>
                  </Text>
                </div>
                <Form.Item name="status" valuePropName="checked">
                  <Checkbox disabled={isInProcess || !list.isLoaded}>
                    Этап работ включен
                  </Checkbox>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    className={styles.button}
                    disabled={!isSubmittable || !list.isLoaded}
                    htmlType="submit"
                    loading={isInProcess}
                  >
                    Сохранить
                  </Button>
                </Form.Item>
                {!!isError && (
                  <Form.Item>
                    <Text type="danger">Не удалось сохранить этап работ</Text>
                  </Form.Item>
                )}
              </Form>
            </Flex>
          </>
        )}
      {!isAddMode && !isEditMode && (
        <Table
          dataSource={list.data}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={!list.isLoaded}
          styles={styles}
        />
      )}
    </MobileInnerWrapper>
  );
};

export default Page;
